import { useEffect, useRef, useState } from 'react'
import { Head } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { createConsumer, type Cable } from '@rails/actioncable'
import { ProjectsLayout } from '@/layouts/projects-layout'
import { FDEConversationList } from '@/components/fde/FDEConversationList'
import { FDEChatPane } from '@/components/fde/FDEChatPane'
import { useFDEConversationState } from '@/hooks/useFDEConversationState'
import type { FDEConversation, FDEMessage, OrganizationBilling } from '@/types/fde'

interface Props {
  conversations: FDEConversation[]
  conversation: FDEConversation
  messages: FDEMessage[]
  hasProcessingMessages: boolean
  organization: OrganizationBilling
}

export default function FDEShow({ conversations, conversation, messages, hasProcessingMessages, organization }: Props) {
  const { t } = useTranslation('fde')

  const state = useFDEConversationState({
    initialConversation: conversation,
    initialMessages: messages,
    initialProcessing: hasProcessingMessages,
    organization,
  })

  // ActionCable subscription
  const consumerRef = useRef<Cable | null>(null)
  const channelRef = useRef<any>(null)
  const handleEventRef = useRef(state.handleChannelEvent)

  useEffect(() => {
    handleEventRef.current = state.handleChannelEvent
  })

  useEffect(() => {
    consumerRef.current = createConsumer()

    channelRef.current = consumerRef.current.subscriptions.create(
      { channel: 'FdeConversationChannel', id: conversation.id },
      {
        received: (data: any) => {
          handleEventRef.current(data)
        },
        rejected: () => {
          toast.error('Connection rejected. You may not have access to this conversation.')
        },
      }
    )

    return () => {
      channelRef.current?.unsubscribe()
      consumerRef.current?.disconnect()
    }
  }, [conversation.id])

  return (
    <>
      <Head title={conversation.title || t('pageTitle')} />
      <div className="flex h-dvh overflow-hidden">
        <FDEConversationList
          conversations={conversations}
          activeConversationId={conversation.id}
        />
        <FDEChatPane
          messages={state.messages}
          processing={state.processing}
          currentActivity={state.currentActivity}
          isSendingMessage={state.isSendingMessage}
          onSendMessage={state.sendMessage}
        />
      </div>
    </>
  )
}

FDEShow.layout = (page: React.ReactNode) => <ProjectsLayout>{page}</ProjectsLayout>
